import { ReactElement, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Button,
  Checkbox,
  Heading,
  Label,
  Paragraph,
  Tag,
  Textarea,
  ValidationMessage,
} from '@digdir/designsystemet-react';
import { ArrowLeftIcon } from '@navikt/aksel-icons';
import type { TemplateLanguageView, TranslationEntry, TranslationItem } from '../../../../api/hms';
import { languageNameInText } from '../../hmsLanguages';
import { groupTranslationItems, textsToSuggest, translationKindLabel, translationRows } from '../languagesView';
import layout from '../../hmsPage.module.css';
import classes from '../HRRiskFactors.module.css';

type Props = {
  language: TemplateLanguageView;
  onBack: () => void;
  onSave: (entries: TranslationEntry[]) => void;
  saveFailed: boolean;
  onSuggest: () => void;
  suggesting: boolean;
  suggestFailed: boolean;
};

export const TranslationView = ({
  language,
  onBack,
  onSave,
  saveFailed,
  onSuggest,
  suggesting,
  suggestFailed,
}: Props): ReactElement => {
  const { t } = useTranslation();
  const [missingOnly, setMissingOnly] = useState(false);
  // What HR types, until the field loses focus and is saved
  const [editing, setEditing] = useState<Record<string, string>>({});
  const name = languageNameInText(language, t);
  const shown = language.items.filter((item) => !missingOnly || !item.translated);
  const todo = textsToSuggest(language.items);
  const drafts = language.items.filter((item) => item.aiDraft);

  const groups = groupTranslationItems(shown);
  const groupTitle = (group: (typeof groups)[number]) =>
    group.factorName ?? t(`hrRiskFactors.translate.${group.kind}`);

  const save = (item: TranslationItem) => {
    const value = editing[item.key];
    if (value === undefined) return;
    setEditing((current) => {
      const next = { ...current };
      delete next[item.key];
      return next;
    });
    if (value.trim() !== item.text.trim() || item.aiDraft) onSave([{ key: item.key, text: value, aiDraft: false }]);
  };

  return (
    <section aria-labelledby="hr-rf-translate" className={layout.card} data-gap="md">
      <Button variant="tertiary" data-size="sm" onClick={onBack} className={classes.backButton}>
        <ArrowLeftIcon aria-hidden />
        {t('hrRiskFactors.version.backToFactors')}
      </Button>
      <div className={classes.translateHeading}>
        <div className={layout.cardHeading}>
          <Heading level={2} data-size="md" id="hr-rf-translate">
            {t('hrRiskFactors.translate.title', { language: name })}
          </Heading>
          <Paragraph data-size="sm" role="status" className={layout.subtle}>
            {t('hrRiskFactors.translate.progress', { translated: language.translated, total: language.total })}
          </Paragraph>
        </div>
        <Checkbox
          data-size="sm"
          label={t('hrRiskFactors.translate.missingOnly')}
          checked={missingOnly}
          onChange={(event) => setMissingOnly(event.target.checked)}
        />
      </div>

      <div className={classes.aiBox}>
        <div className={classes.aiRow}>
          <div className={classes.aiText}>
            <span className="ds-label" data-size="sm">
              {t('hrRiskFactors.translate.ai.title')}
            </span>
            <span className={`ds-paragraph ${layout.subtle}`} data-size="xs">
              {t('hrRiskFactors.translate.ai.description')}
            </span>
          </div>
          {todo.length > 0 && (
            <Button variant="secondary" data-size="sm" onClick={onSuggest} loading={suggesting}>
              {suggesting
                ? t('hrRiskFactors.translate.ai.busy')
                : t('hrRiskFactors.translate.ai.suggest', { count: todo.length })}
            </Button>
          )}
        </div>
        {drafts.length > 0 && (
          <div className={classes.aiDrafts}>
            <span className="ds-paragraph" data-size="sm" role="status">
              {t('hrRiskFactors.translate.ai.drafts', { count: drafts.length })}
            </span>
            <Button
              variant="tertiary"
              data-size="sm"
              onClick={() => onSave(drafts.map((item) => ({ key: item.key, text: item.text, aiDraft: false })))}
            >
              {t('hrRiskFactors.translate.ai.approveAll')}
            </Button>
          </div>
        )}
        {suggestFailed && <ValidationMessage>{t('hrRiskFactors.translate.ai.failed')}</ValidationMessage>}
      </div>

      {saveFailed && (
        <Alert data-color="danger">
          <Paragraph data-size="sm">{t('hms.errors.save')}</Paragraph>
        </Alert>
      )}

      {groups.map((group) => (
        <div key={groupTitle(group)} className={classes.translateGroup}>
          <Heading level={3} data-size="2xs">
            {groupTitle(group)}
          </Heading>
          {group.items.map((item) => {
            const id = `hr-rf-tr-${item.key}`;
            const kind = translationKindLabel(item, t);
            return (
              <div key={item.key} className={classes.translateRow}>
                <div className={classes.translateSource}>
                  <span className={`ds-paragraph ${layout.subtle}`} data-size="xs">
                    {t('hrRiskFactors.translate.source', { kind })}
                  </span>
                  <span className="ds-paragraph" data-size="sm" lang="nb">
                    {item.source}
                  </span>
                </div>
                <div className={classes.translateField}>
                  <Label htmlFor={id} data-size="xs" className={classes.translateLabel}>
                    {item.standard
                      ? t('hrRiskFactors.translate.targetStandard', { kind, language: name })
                      : t('hrRiskFactors.translate.target', { kind, language: name })}
                  </Label>
                  <Textarea
                    id={id}
                    data-size="sm"
                    rows={translationRows(item)}
                    lang={language.code}
                    dir={language.direction}
                    placeholder={t(
                      item.standard ? 'hrRiskFactors.translate.placeholderStandard' : 'hrRiskFactors.translate.placeholder',
                    )}
                    aria-describedby={item.aiDraft ? `${id}-ai` : undefined}
                    value={editing[item.key] ?? item.text}
                    onChange={(event) => setEditing((current) => ({ ...current, [item.key]: event.target.value }))}
                    onBlur={() => save(item)}
                  />
                  {item.aiDraft && (
                    <div id={`${id}-ai`} className={classes.aiDraft}>
                      <Tag data-size="sm" data-color="info">
                        {t('hrRiskFactors.translate.ai.draftTag')}
                      </Tag>
                      <Button
                        variant="tertiary"
                        data-size="sm"
                        onClick={() => onSave([{ key: item.key, text: editing[item.key] ?? item.text, aiDraft: false }])}
                      >
                        {t('hrRiskFactors.translate.ai.approve')}
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ))}
      {shown.length === 0 && (
        <Alert data-color="success">
          <Paragraph data-size="sm">{t('hrRiskFactors.translate.done', { language: name })}</Paragraph>
        </Alert>
      )}
    </section>
  );
};
